import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSupabaseClient } from '../lib/supabase';
import { payWithRazorpay, pollPaymentStatus } from '../lib/razorpay';
import { toast } from 'sonner';

export const PaymentBottomSheet = ({
  isOpen,
  onClose,
  amount,
  tenantId,
  branchId,
  onCreateOrder,
  onPaymentConfirmed,
  paymentStatusTable = 'orders'
}: {
  isOpen: boolean;
  onClose: () => void;
  amount: number;
  tenantId: string;
  branchId?: string | null;
  onCreateOrder: (paymentMethod: 'cod' | 'wallet' | 'wallet_plus_online' | 'online') => Promise<{ order_id?: string; booking_id?: string; id?: string; amount_pending: number; wallet_amount_used?: number; [key: string]: any }>;
  onPaymentConfirmed: (result: any) => void;
  paymentStatusTable?: string;
}) => {
  const { user } = useAuth();
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [loadingWallet, setLoadingWallet] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  useEffect(() => {
    if (!isOpen || !user) return;
    const fetchWallet = async () => {
      setLoadingWallet(true);
      const supabase = getSupabaseClient();
      if (!supabase) { setLoadingWallet(false); return; }
      const { data } = await supabase.from('wallet_accounts').select('current_balance').eq('user_id', user.id).eq('account_type', 'consumer').maybeSingle();
      setWalletBalance(data?.current_balance ?? 0);
      setLoadingWallet(false);
    };
    fetchWallet();
  }, [isOpen, user]);

  const openRazorpayFor = async (payAmount: number, recordId: string) => {
    const isRoomBooking = paymentStatusTable === 'room_bookings';
    const refType = isRoomBooking ? 'room_booking' : 'orders';

    return new Promise<void>((resolve, reject) => {
      payWithRazorpay({
        tenantId,
        branchId: branchId || null,
        purpose: 'consumer_order',
        amount: payAmount,
        referenceType: refType,
        referenceId: recordId,
        payerType: 'consumer',
        payerId: user?.id,
        prefillName: user?.user_metadata?.full_name || '',
        prefillPhone: user?.phone || '',
        prefillEmail: user?.email || '',
        onSuccess: async () => {
          setIsConfirming(true);
          // Handle race condition: webhook might take 1-2 seconds to update DB
          await pollPaymentStatus({
            table: paymentStatusTable,
            id: recordId,
            targetStatus: 'paid',
            maxAttempts: 4,
            delayMs: 1500
          });
          setIsConfirming(false);
          resolve();
        },
        onFailure: (err) => {
          reject(err);
        }
      });
    });
  };

  const handleCash = async () => {
    setProcessing('cod');
    try {
      const result = await onCreateOrder('cod');
      onPaymentConfirmed(result);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to place order');
    } finally {
      setProcessing(null);
    }
  };

  const handleWallet = async () => {
    if (walletBalance === null) return;
    setProcessing('wallet');
    let orderResult: any = null;
    try {
      const method = walletBalance >= amount ? 'wallet' : 'wallet_plus_online';
      orderResult = await onCreateOrder(method);
      const recordId = orderResult.booking_id || orderResult.order_id || orderResult.id;

      if (orderResult.amount_pending > 0 && recordId) {
        toast('Paying ₹' + (orderResult.wallet_amount_used || 0).toFixed(2) + ' from Wallet, remaining ₹' + orderResult.amount_pending.toFixed(2) + ' via online payment');
        await openRazorpayFor(orderResult.amount_pending, recordId);
      }
      onPaymentConfirmed(orderResult);
      onClose();
    } catch (err: any) {
      if (orderResult) {
        // Order or booking was created in DB
        if (paymentStatusTable === 'room_bookings') {
          toast.info("Booking ban gayi hai, payment baad mein bhi kar sakte hain 'My Bookings' se");
          onPaymentConfirmed({ ...orderResult, payment_status: 'unpaid' });
          onClose();
        } else {
          toast.error(err.message || 'Payment cancelled');
          onPaymentConfirmed(orderResult);
          onClose();
        }
      } else {
        toast.error(err.message || 'Payment failed');
      }
    } finally {
      setProcessing(null);
    }
  };

  const handleOnline = async () => {
    setProcessing('online');
    let orderResult: any = null;
    try {
      orderResult = await onCreateOrder('online');
      const pending = orderResult.amount_pending ?? orderResult.total_amount ?? amount;
      const recordId = orderResult.booking_id || orderResult.order_id || orderResult.id;

      if (pending > 0 && recordId) {
        await openRazorpayFor(pending, recordId);
      }
      onPaymentConfirmed(orderResult);
      onClose();
    } catch (err: any) {
      if (orderResult) {
        // Order or booking was created in DB
        if (paymentStatusTable === 'room_bookings') {
          toast.info("Booking ban gayi hai, payment baad mein bhi kar sakte hain 'My Bookings' se");
          onPaymentConfirmed({ ...orderResult, payment_status: 'unpaid' });
          onClose();
        } else {
          toast.error(err.message || 'Payment cancelled');
          onPaymentConfirmed(orderResult);
          onClose();
        }
      } else {
        toast.error(err.message || 'Payment failed');
      }
    } finally {
      setProcessing(null);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white dark:bg-slate-950 rounded-t-3xl sm:rounded-3xl w-full sm:max-w-sm p-6 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">Choose Payment Method</h3>
          <button onClick={onClose} disabled={!!processing || isConfirming} className="text-slate-400"><X size={20} /></button>
        </div>
        <p className="text-2xl font-extrabold text-primary">₹{amount.toFixed(2)}</p>

        {isConfirming ? (
          <div className="py-8 text-center space-y-3">
            <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto" />
            <p className="font-bold text-slate-800 dark:text-slate-200">Confirming payment with gateway...</p>
            <p className="text-xs text-slate-400">Please wait while we verify your transaction.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <button onClick={handleCash} disabled={!!processing} className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-slate-200 dark:border-slate-800 hover:border-primary transition-colors disabled:opacity-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">💵</div>
                <span className="font-semibold text-slate-900 dark:text-slate-100">Cash / Pay at Counter</span>
              </div>
              {processing === 'cod' && <span className="text-xs text-primary">Placing...</span>}
            </button>

            <button onClick={handleWallet} disabled={!!processing || loadingWallet} className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-slate-200 dark:border-slate-800 hover:border-primary transition-colors disabled:opacity-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">🪙</div>
                <div className="text-left">
                  <p className="font-semibold text-slate-900 dark:text-slate-100">BahiBox Coin</p>
                  <p className="text-xs text-slate-400">{loadingWallet ? 'Loading...' : 'Balance: ₹' + (walletBalance ?? 0).toFixed(2)}</p>
                </div>
              </div>
              {processing === 'wallet' && <span className="text-xs text-primary">Processing...</span>}
            </button>

            <button onClick={handleOnline} disabled={!!processing} className="w-full flex items-center justify-between p-4 rounded-xl border-2 border-slate-200 dark:border-slate-800 hover:border-primary transition-colors disabled:opacity-50">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">💳</div>
                <div className="text-left">
                  <span className="font-semibold text-slate-900 dark:text-slate-100 block">Pay Online (Razorpay)</span>
                  <span className="text-xs text-slate-400">UPI, Cards, Netbanking</span>
                </div>
              </div>
              {processing === 'online' && <span className="text-xs text-primary">Opening Checkout...</span>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

